import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "catalyst_concierge_session";
const CAPTURED_KEY = "catalyst_concierge_captured";

function loadOrCreateSession() {
  let id = null;
  try { id = localStorage.getItem(STORAGE_KEY); } catch { /* SSR / privacy mode */ }
  if (!id) {
    id = `sess_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* noop */ }
  }
  return id;
}

const INITIAL_GREETING =
  "Hi there — I'm the Catalyst Concierge. Ask me anything about our services, or let me know if you'd like a consultant to reach out.";

/**
 * ConciergeChat — floating public chat bubble on all site pages.
 *
 * • Session id persists in localStorage so return visitors keep context.
 * • When the visitor asks about pricing / working together, the bot gently
 *   asks for name+email+org — the frontend then surfaces a slim inline
 *   contact form that, when submitted, is sent as `contact` on the next
 *   message and creates a `leads` row server-side.
 */
export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: INITIAL_GREETING },
  ]);
  const [captured, setCaptured] = useState(() => {
    try { return localStorage.getItem(CAPTURED_KEY) === "1"; } catch { return false; }
  });
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", organization: "" });
  const sessionId = useMemo(loadOrCreateSession, []);
  const scrollRef = useRef(null);

  // Rehydrate transcript on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get(`/chat/concierge/session/${sessionId}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        }
        if (data.captured) {
          setCaptured(true);
          try { localStorage.setItem(CAPTURED_KEY, "1"); } catch { /* noop */ }
        }
      })
      .catch(() => { /* first-time visit or offline — keep default greeting */ });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const sendMessage = async (contactPayload = null) => {
    const text = input.trim();
    if (!text && !contactPayload) return;
    if (text) setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const body = { session_id: sessionId, message: text || "Here are my details:" };
      if (contactPayload) body.contact = contactPayload;
      const { data } = await api.post("/chat/concierge", body);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data.captured) {
        setCaptured(true);
        try { localStorage.setItem(CAPTURED_KEY, "1"); } catch { /* noop */ }
        setContactOpen(false);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I couldn't reach the assistant right now. Please try again in a moment, or book a consultation at /book." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email) return;
    await sendMessage({ ...contact });
    setContact({ name: "", email: "", organization: "" });
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-[var(--navy)] text-white h-14 px-5 shadow-xl hover:bg-[var(--navy-2)] transition-colors flex items-center gap-2.5 font-bold text-sm tracking-wide group"
          data-testid="concierge-open"
          aria-label="Open Catalyst chat"
        >
          <MessageCircle size={18} />
          <span className="hidden sm:inline">Chat with us</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--gold)] rounded-full ring-2 ring-[var(--cream)] group-hover:animate-ping" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] h-[560px] max-h-[calc(100vh-64px)] bg-white border border-[var(--line)] shadow-2xl flex flex-col"
          data-testid="concierge-panel"
        >
          {/* Header */}
          <div className="bg-[var(--navy)] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] text-[var(--gold)] font-bold">CATALYST CONCIERGE</div>
              <div className="font-display text-lg leading-tight">How can we help?</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1"
              aria-label="Close chat"
              data-testid="concierge-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[var(--cream)]" data-testid="concierge-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[var(--navy)] text-white"
                      : "bg-white text-[var(--ink)] border border-[var(--line)]"
                  }`}
                  data-testid={`concierge-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-[var(--muted)] border border-[var(--line)] px-3.5 py-2.5 text-[13px] flex items-center gap-2">
                  <Loader2 className="animate-spin" size={12} /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Contact capture (inline) */}
          {contactOpen && !captured && (
            <form
              onSubmit={submitContact}
              className="border-t border-[var(--line)] bg-white p-4 space-y-2.5"
              data-testid="concierge-contact-form"
            >
              <div className="text-xs font-bold text-[var(--navy)] uppercase tracking-wider">Share your details</div>
              <input
                required
                placeholder="Your name"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                data-testid="concierge-contact-name"
              />
              <input
                required type="email"
                placeholder="Email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                data-testid="concierge-contact-email"
              />
              <input
                placeholder="Organization (optional)"
                value={contact.organization}
                onChange={(e) => setContact({ ...contact, organization: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                data-testid="concierge-contact-org"
              />
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={sending} className="btn btn-navy !py-2 !px-4 !text-[11px] disabled:opacity-60 flex-1 justify-center" data-testid="concierge-contact-submit">
                  {sending ? <Loader2 className="animate-spin" size={12} /> : "Send my info"}
                </button>
                <button type="button" onClick={() => setContactOpen(false)} className="btn btn-ghost !py-2 !px-3 !text-[11px]">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Composer */}
          {!contactOpen && (
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="border-t border-[var(--line)] bg-white p-3 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                disabled={sending}
                data-testid="concierge-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="bg-[var(--navy)] text-white h-9 w-9 flex items-center justify-center hover:bg-[var(--navy-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="concierge-send"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </form>
          )}

          {/* Contact CTA */}
          {!contactOpen && !captured && (
            <button
              onClick={() => setContactOpen(true)}
              className="text-[11px] text-[var(--muted)] hover:text-[var(--navy)] py-2 border-t border-[var(--line)] bg-white transition-colors font-bold tracking-wider uppercase"
              data-testid="concierge-share-details"
            >
              Have a consultant reach out →
            </button>
          )}
          {captured && (
            <div className="text-[11px] text-green-700 py-2 border-t border-[var(--line)] bg-white text-center font-bold tracking-wider uppercase">
              ✓ We have your details — a consultant will be in touch
            </div>
          )}
        </div>
      )}
    </>
  );
}
